import React, { useRef } from "react";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { useFieldArray, useFormContext } from "react-hook-form";
import Image from "@components/item/Image";
import { useTheme } from "@emotion/react";
import useDropdownStore from "@store/useDropdownStore";
import { useApiRequest } from "@utils/ApiRequest";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import useSelectedColorStore from "@store/register/useSelectedColor";
import { useTranslation } from "react-i18next";

type Props = {
  name: string; // "option_list[0].size_list" 형태
};

const SizeSelect = ({ name }: Props) => {
  const { getSizeWithStock } = useApiRequest();
  const params = useParams();
  const { colorId } = useSelectedColorStore();
  const { t } = useTranslation();
  const lang = window.localStorage.getItem("language");

  const { data: sizeList } = useQuery({
    queryKey: ["sizeList", params.productId, colorId],
    queryFn: () => getSizeWithStock(Number(params.productId), colorId),
    enabled: !!params.productId && !!colorId,
  });

  const { dropdown, setDropdown } = useDropdownStore();

  const { control, watch } = useFormContext();

  const buttonRef = useRef<HTMLButtonElement>(null);

  const theme = useTheme();

  const { append } = useFieldArray({
    control,
    name,
  });

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDropdown(name);

    if (name === dropdown) {
      setDropdown("");
    } else {
      setDropdown(name);
    }
    if (!sizeList || sizeList.length === 0) return;
  };

  const handleChange = (sizeName: string, sizeId: number, amount: number) => {
    // 현재 size_list에서 이미 선택된 사이즈인지 확인
    const currentSizeList = watch(name) || [];

    const existingSize = currentSizeList.find(
      (size: any) => size.size_id === sizeId,
    );

    if (!existingSize) {
      // 새로운 사이즈 추가
      append({
        size_id: sizeId,
        size_name: sizeName,
        amount: 1, // 초기값 1로 설정
        maxAmount: amount, // 재고량은 따로 저장
      });
    }
  };

  return (
    <Container>
      <Button onClick={handleToggle} type="button" ref={buttonRef}>
        <Value>{t("사이즈 선택")}</Value>

        <Image
          src={
            name === dropdown
              ? theme.image.arrow_up_gray.default
              : theme.image.arrow_down_gray.default
          }
        />
      </Button>
      {name === dropdown && (
        <SelectBox
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.1 }}>
          {sizeList?.map((item: any, index: number) => (
            <OptionItem
              type="button"
              key={item.code}
              last={index === sizeList.length - 1}
              disabled={item.amount === 0}
              onClick={() => handleChange(item.name, item.id, item.amount)}>
              <span>{item.name}</span>
              <span className="amount">
                {item.amount === 0
                  ? t("재고없음")
                  : lang === "en"
                    ? `${t("개수")}: ${item.amount} `
                    : `${item.amount} ${t("개수")}`}
              </span>
            </OptionItem>
          ))}
        </SelectBox>
      )}
    </Container>
  );
};

export default SizeSelect;

const Container = styled.div({
  position: "relative",
  width: "100%",
  zIndex: 1,
});

const Button = styled.button(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "0.4rem",
  width: "100%",
  fontSize: "1.6rem",
  fontWeight: theme.fontWeight.regular,
  lineHeight: "2rem",
  padding: "1.6rem",
  border: `1px solid ${theme.color.light_gray}`,
}));

const Value = styled.div(({ theme }) => ({
  color: theme.color.light_gray,
}));

const SelectBox = styled(motion.div)<{}>(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",

  backgroundColor: theme.color.white,
  border: `1px solid ${theme.color.light_gray}`,
  overflowY: "auto",
  maxHeight: "256px",
  zIndex: 10000,
  "::-webkit-scrollbar": {
    display: "none",
  },
}));

const OptionItem = styled.button<{ last: boolean; disabled: boolean }>(
  ({ theme, last, disabled }) => ({
    width: "100%",
    padding: "10px 30px 10px 20px",
    display: "flex",
    justifyContent: "space-between",
    gap: "1rem",
    alignItems: "center",
    fontSize: "1.6rem",
    lineHeight: "2.4rem",
    fontWeight: theme.fontWeight.regular,
    color: disabled ? theme.color.light_gray : theme.color.black,
    backgroundColor: theme.color.white,
    border: "none",
    textAlign: "start",
    borderBottom: last ? "none" : `1px solid ${theme.color.border_gray}`,
    ":hover": {
      backgroundColor: disabled ? "none" : theme.color.border_gray,
    },

    ".amount": {
      color: disabled ? theme.color.light_gray : theme.color.gray,
    },
  }),
);
